// src/hooks/useAnalytics.js
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import analyticsService from '../services/analyticsService';

// Hook for tracking page views automatically
export const usePageTracking = () => {
  const location = useLocation();
  const prevLocation = useRef();

  useEffect(() => {
    // Track page view when location changes
    if (prevLocation.current !== location.pathname) {
      const pageName = location.pathname === '/' ? 'home' : location.pathname.slice(1);
      analyticsService.trackPageView(pageName);
      prevLocation.current = location.pathname;
    }
  }, [location]);
};

// Hook for tracking engagement time
export const useEngagementTracking = (pageName) => {
  const startTime = useRef(Date.now());
  const isActive = useRef(true);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isActive.current = false;
      } else {
        isActive.current = true;
        startTime.current = Date.now();
      }
    };

    const handleBeforeUnload = () => {
      if (isActive.current) {
        const timeSpent = Date.now() - startTime.current;
        analyticsService.trackEngagementTime(pageName, timeSpent);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Track engagement time on cleanup
      if (isActive.current) {
        const timeSpent = Date.now() - startTime.current;
        analyticsService.trackEngagementTime(pageName, timeSpent);
      }
    };
  }, [pageName]);
};

// Main analytics hook
export const useAnalytics = () => {
  return {
    trackPageView: analyticsService.trackPageView.bind(analyticsService),
    trackBlogView: analyticsService.trackBlogView.bind(analyticsService),
    trackSearch: analyticsService.trackSearch.bind(analyticsService),
    trackSocialShare: analyticsService.trackSocialShare.bind(analyticsService),
    trackEngagementTime: analyticsService.trackEngagementTime.bind(analyticsService),
    trackCustomEvent: analyticsService.trackCustomEvent.bind(analyticsService),
    setUserProperty: analyticsService.setUserProperty.bind(analyticsService)
  };
};